import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// Solo como TIPO: los DTO arrastran `class-validator` y `@nestjs/mapped-types`,
// que no resuelven en el navegador. `import type` se borra al compilar.
import type { UpdateVoucherConfigDto, VoucherConfigResponseDto } from '@bandido/shared/dtos';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

import {
  VoucherPreviewBody,
  VoucherPreviewResult,
  voucherPreviewOutcome,
} from './voucherPreview';

export const voucherConfigKey = (agencyId: number) => [QueryKeys.getVoucherConfig, agencyId];

/**
 * LA CONFIGURACIÓN de la agencia: identidad, bloques y los TOPES con los que
 * el backend valida. La pantalla dibuja con esto y nada más.
 */
export const useVoucherConfig = (agencyId: number) =>
  useQuery({
    queryKey: voucherConfigKey(agencyId),
    queryFn: async (): Promise<VoucherConfigResponseDto> => {
      const response = await client.get<VoucherConfigResponseDto>(`vouchers/config/agency/${agencyId}`);
      return response.data;
    },
    enabled: Number.isFinite(agencyId),
    retry: false,
  });

/**
 * Guarda la configuración. El cuerpo lo arman `voucherIdentityPayload` y
 * `voucherTextsPayload`: los campos de identidad que cambiaron más los bloques
 * de texto que cambiaron. Sin cambios de texto la clave `blocks` no viaja, y
 * el `PUT` solo toca los bloques que el cuerpo nombra.
 *
 * La respuesta del `PUT` es la configuración completa releída, así que se
 * siembra la caché con ella en vez de invalidar y volver a pedir.
 */
export const useSaveVoucherConfig = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateVoucherConfigDto): Promise<VoucherConfigResponseDto> => {
      const response = await client.put<VoucherConfigResponseDto>(`vouchers/config/agency/${agencyId}`, body);
      return response.data;
    },
    /*
     * Un `GET` que ya estaba viajando cuando arranca el `PUT` resuelve DESPUÉS
     * y pisa la caché sembrada con datos previos al guardado: el borrador
     * —intacto respecto de su base— adopta lo viejo y el asesor ve su texto
     * recién guardado volver atrás, con el toast verde todavía en pantalla.
     * Cancelarlo antes de escribir es lo que cierra esa ventana.
     */
    onMutate: () => queryClient.cancelQueries(voucherConfigKey(agencyId)),
    onSuccess: (config) => {
      queryClient.setQueryData(voucherConfigKey(agencyId), config);
    },
  });
};

/**
 * LA VISTA PREVIA: un `POST` que contesta un PDF o un JSON de estado por el
 * MISMO camino, así que se pide como blob y se decide por el `content-type`.
 *
 * El 400 también viaja como blob —lo pidió el cliente— y hay que leerlo para
 * poder mostrar qué bloque lo causó: sin esto, el rechazo llegaba como un
 * objeto vacío y la pantalla no tenía nada que decir.
 */
export async function fetchVoucherPreview(body: VoucherPreviewBody): Promise<VoucherPreviewResult> {
  try {
    const response = await client.post<Blob>('vouchers/preview', body, { responseType: 'blob' });
    const contentType = response.headers?.['content-type'];
    const json = (contentType ?? '').includes('application/pdf')
      ? undefined
      : ((await leerJson(response.data)) as { status?: string; reason?: string } | undefined);
    return voucherPreviewOutcome({ contentType, blob: response.data, json });
  } catch (error) {
    throw await conErrorLegible(error);
  }
}

/** El cuerpo de un error que vino como blob, puesto donde el lector lo busca. */
async function conErrorLegible(error: unknown): Promise<unknown> {
  const response = (error as { response?: { data?: unknown } } | null)?.response;
  if (!response || !(response.data instanceof Blob)) return error;
  response.data = (await leerJson(response.data)) ?? {};
  return error;
}

/** El blob como JSON, o `undefined` si no lo es. Nunca tira. */
async function leerJson(blob: Blob): Promise<unknown> {
  try {
    return JSON.parse(await blob.text());
  } catch {
    return undefined;
  }
}

/**
 * Sube un logo. Va por su propio endpoint y ocurre al ELEGIR el archivo, no al
 * Guardar: el backend valida el formato por los bytes y responde la
 * configuración entera, así que el resultado se ve en el acto.
 */
export const useUploadVoucherLogo = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { kind: 'primary' | 'negative'; file: File }): Promise<VoucherConfigResponseDto> => {
      const body = new FormData();
      body.append('file', input.file);
      const response: AxiosResponse<VoucherConfigResponseDto> = await client.post(
        `vouchers/config/agency/${agencyId}/logo/${input.kind}`,
        body,
      );
      return response.data;
    },
    onSuccess: (config) => {
      queryClient.setQueryData(voucherConfigKey(agencyId), config);
    },
  });
};
